document.addEventListener('alpine:init', () => {
  Alpine.data('LuxuryNav', () => ({
    activeMenu: null,
    hoveredChild: null,
    _leaveTimeout: null,
    _paneRAF: null,
    _scrollRaf: null,
    _lastPanelAnchorPx: '',

    get isPanelOpen() {
      return this.activeMenu !== null;
    },

    init() {
      // Attach mouseleave to the full-width <nav> ancestor so the close
      // debounce only fires when the cursor leaves the entire nav bar,
      // not when it briefly exits the narrow luxury-nav wrapper on its
      // way down to the fixed panel below the header.
      const navEl = this.$el.closest('nav');
      if (navEl) {
        navEl.addEventListener('mouseleave', () => this.leaveNav());
      }

      // Always refresh on scroll + resize. If we skip scroll while the menu is
      // open, the anchor stays stuck at the pre-scroll value → wrong gap until
      // close/reopen. Repositioning can fire a stray mouseleave; we clear a
      // pending close only when the anchor value actually changes (see below).
      this._boundOnResize = () => this._schedulePanelAnchorUpdate();
      this._boundOnScroll = () => this._schedulePanelAnchorUpdate();
      this._updatePanelAnchor();
      window.addEventListener('scroll', this._boundOnScroll, { passive: true });
      window.addEventListener('resize', this._boundOnResize);

      this.$watch('activeMenu', (value) => {
        if (value !== null) {
          this._activatePane(value);
          requestAnimationFrame(() => {
            this._updatePanelAnchor();
            requestAnimationFrame(() => this._updatePanelAnchor());
          });
        } else {
          this._updatePanelAnchor();
        }
      });
    },

    destroy() {
      window.removeEventListener('scroll', this._boundOnScroll);
      window.removeEventListener('resize', this._boundOnResize);
      if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    },

    _schedulePanelAnchorUpdate() {
      if (this._scrollRaf != null) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        this._updatePanelAnchor();
      });
    },

    _updatePanelAnchor() {
      if (!this.$el) return;

      // Anchor to the sticky Header *section* wrapper (schema class section-header),
      // not the whole header-group — the group box often sits lower than the real
      // chrome and Math.max(group, header) created a visible gap (worse after scroll).
      const stickySection =
        this.$el.closest('.section-header') ||
        document.querySelector('[data-theme-header]');
      const headerGroup = document.querySelector(
        '.shopify-section-group-header-group'
      );

      let bottom = 0;
      if (stickySection) {
        bottom = stickySection.getBoundingClientRect().bottom;
      } else if (headerGroup) {
        bottom = headerGroup.getBoundingClientRect().bottom;
      }

      if (bottom > 0) {
        const px = `${Math.round(bottom)}px`;
        if (this.activeMenu !== null && px !== this._lastPanelAnchorPx) {
          this.cancelLeave();
        }
        this._lastPanelAnchorPx = px;
        this.$el.style.setProperty('--luxury-nav-panel-top', px);
      }
    },

    enterItem(index) {
      if (this._leaveTimeout) {
        clearTimeout(this._leaveTimeout);
        this._leaveTimeout = null;
      }
      // Reset hovered child when switching to a different pane
      if (this.activeMenu !== index) {
        this.hoveredChild = null;
      }
      this.activeMenu = index;
    },

    leaveNav() {
      if (this._leaveTimeout) clearTimeout(this._leaveTimeout);
      this._leaveTimeout = setTimeout(() => {
        this.activeMenu = null;
        this.hoveredChild = null;
        this._deactivateAllPanes();
      }, 200);
    },

    cancelLeave() {
      if (this._leaveTimeout) {
        clearTimeout(this._leaveTimeout);
        this._leaveTimeout = null;
      }
    },

    closeMenu() {
      if (this._leaveTimeout) clearTimeout(this._leaveTimeout);
      this.activeMenu = null;
      this.hoveredChild = null;
      this._deactivateAllPanes();
    },

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closeMenu();
        const active = this.$el.querySelector('.luxury-nav__link[aria-expanded="true"]');
        if (active) active.focus();
        return;
      }

      const triggers = [...this.$el.querySelectorAll('[data-luxury-trigger]')];
      const focused = document.activeElement;
      const idx = triggers.indexOf(focused);

      if (idx === -1) return;

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        const next = triggers[(idx + 1) % triggers.length];
        next.focus();
        const nextIndex = next.dataset.luxuryTrigger;
        if (next.dataset.luxuryHasChildren === 'true') {
          this.enterItem(parseInt(nextIndex, 10));
        } else {
          this.closeMenu();
        }
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        const prev = triggers[(idx - 1 + triggers.length) % triggers.length];
        prev.focus();
        const prevIndex = prev.dataset.luxuryTrigger;
        if (prev.dataset.luxuryHasChildren === 'true') {
          this.enterItem(parseInt(prevIndex, 10));
        } else {
          this.closeMenu();
        }
      }
    },

    _activatePane(index) {
      if (this._paneRAF) cancelAnimationFrame(this._paneRAF);

      const panel = this.$refs.panel;
      if (!panel) return;

      const panes = panel.querySelectorAll('[data-luxury-pane]');
      panes.forEach((pane) => {
        const paneIndex = parseInt(pane.dataset.luxuryPane, 10);
        if (paneIndex === index) {
          pane.classList.add('luxury-nav__pane--active');
          // Double-rAF: the first frame registers display:block,
          // the second starts the opacity/transform transition.
          // Without this, browsers skip the transition on the very first open.
          this._paneRAF = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              pane.classList.add('luxury-nav__pane--visible');
            });
          });
        } else {
          pane.classList.remove('luxury-nav__pane--visible');
          pane.addEventListener('transitionend', function handler() {
            if (!pane.classList.contains('luxury-nav__pane--visible')) {
              pane.classList.remove('luxury-nav__pane--active');
            }
            pane.removeEventListener('transitionend', handler);
          });

          if (!pane.classList.contains('luxury-nav__pane--visible')) {
            pane.classList.remove('luxury-nav__pane--active');
          }
        }
      });
    },

    _deactivateAllPanes() {
      const panel = this.$refs.panel;
      if (!panel) return;

      const panes = panel.querySelectorAll('[data-luxury-pane]');
      panes.forEach((pane) => {
        pane.classList.remove('luxury-nav__pane--visible');
        setTimeout(() => {
          pane.classList.remove('luxury-nav__pane--active');
        }, 350);
      });
    },
  }));
});
